'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Location {
  id: string;
  name: string;
  region: string;
  deliveryAvailable: boolean;
}

interface LocationSelectorProps {
  onLocationChange?: (location: Location) => void;
}

const LocationSelector = ({ onLocationChange }: LocationSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location>({
    id: '1',
    name: 'Gurgaon',
    region: 'Haryana',
    deliveryAvailable: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const locations: Location[] = [
    { id: '1', name: 'Gurgaon', region: 'Haryana', deliveryAvailable: true },
    { id: '2', name: 'Faridabad', region: 'Haryana', deliveryAvailable: true },
    { id: '3', name: 'Panchkula', region: 'Haryana', deliveryAvailable: true },
    { id: '4', name: 'Ambala', region: 'Haryana', deliveryAvailable: true },
    { id: '5', name: 'Karnal', region: 'Haryana', deliveryAvailable: true },
    { id: '6', name: 'Panipat', region: 'Haryana', deliveryAvailable: true },
    { id: '7', name: 'Rohtak', region: 'Haryana', deliveryAvailable: true },
    { id: '8', name: 'Hisar', region: 'Haryana', deliveryAvailable: false },
  ];

  const filteredLocations = locations.filter(
    (location) =>
      location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.region.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    setIsOpen(false);
    setSearchQuery('');
    onLocationChange?.(location);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-md transition-smooth hover:bg-muted hover:shadow-elevation-sm focus-ring"
        aria-label="Select delivery location"
        aria-expanded={isOpen}
      >
        <Icon name="MapPinIcon" size={18} className="text-primary" />
        <div className="flex flex-col items-start">
          <span className="text-xs font-caption text-muted-foreground leading-none">Deliver to</span>
          <span className="text-sm font-medium text-foreground leading-none mt-0.5">
            {selectedLocation.name}
          </span>
        </div>
        <Icon 
          name="ChevronDownIcon" 
          size={16} 
          className={`text-muted-foreground transition-smooth ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-popover border border-border rounded-md shadow-elevation-lg animate-slide-down z-[110]">
          <div className="p-4 border-b border-border">
            <div className="relative">
              <Icon 
                name="MagnifyingGlassIcon" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-md text-sm focus-ring"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredLocations.length > 0 ? (
              filteredLocations.map((location) => (
                <button
                  key={location.id}
                  onClick={() => handleLocationSelect(location)}
                  className={`w-full flex items-start gap-3 px-4 py-3 transition-smooth hover:bg-muted ${
                    selectedLocation.id === location.id ? 'bg-muted' : ''
                  }`}
                >
                  <Icon 
                    name={selectedLocation.id === location.id ? 'CheckCircleIcon' : 'MapPinIcon'} 
                    size={20} 
                    className={selectedLocation.id === location.id ? 'text-primary' : 'text-muted-foreground'}
                  />
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-foreground">{location.name}</span>
                      {location.deliveryAvailable ? (
                        <span className="text-xs font-caption text-success">Available</span>
                      ) : (
                        <span className="text-xs font-caption text-error">Unavailable</span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">{location.region}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <Icon name="MapPinIcon" size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No locations found</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-border">
            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift">
              <Icon name="PlusCircleIcon" size={18} />
              <span>Add New Location</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;